import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Text,
} from "@chakra-ui/react";
import { useCognito } from "../utils/useCognito";

const UpdateShippingModal = ({ isOpen, onClose, orderId, onSuccess }) => {
  const [carrier, setCarrier] = useState("FedEx");
  const [customCarrier, setCustomCarrier] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const { getIdToken } = useCognito();

  const extractTrackingNumber = (input) => {
    try {
      const url = new URL(input);
      const params = new URLSearchParams(url.search);
      return params.get("trknbr") || params.get("tracknumbers") || null;
    } catch {
      return null;
    }
  };

  const handlePaste = (e) => {
    e.preventDefault(); // 🛑 Stop the raw text from being pasted into the field
  
    const pasted = e.clipboardData.getData("text");
    const extracted = extractTrackingNumber(pasted);
  
    if (extracted) {
      setTrackingNumber(extracted); // ✅ Only insert the extracted value
      toast({
        title: "Tracking number extracted",
        description: `Using: ${extracted}`,
        status: "info",
        duration: 4000,
        isClosable: true,
      });
    } else {
      setTrackingNumber(""); // Clear the field if invalid
      toast({
        title: "Could not extract tracking number",
        description: "Please enter it manually.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }
  };
  

  const handleSubmit = async () => {
    if (!trackingNumber.trim()) {
      toast({
        title: "Tracking number required",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const selectedCarrier = carrier === "Custom" ? customCarrier : carrier;

    try {
      setSubmitting(true);
      const token = getIdToken();

      const res = await fetch(
        "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/updateOrderShippingInfo",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            orderId,
            carrier: selectedCarrier,
            trackingNumber,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to update shipping info");

      toast({
        title: "Shipping info updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Shipping update failed:", err);
      toast({
        title: "Update failed",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isTrackingValid = trackingNumber.trim().length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update Shipping Info</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={4}>
            <FormLabel>Carrier</FormLabel>
            <Select value={carrier} onChange={(e) => setCarrier(e.target.value)}>
              <option value="FedEx">FedEx</option>
              <option value="Custom">Custom</option>
            </Select>
          </FormControl>

          {carrier === "Custom" && (
            <FormControl mb={4}>
              <FormLabel>Custom Carrier</FormLabel>
              <Input
                value={customCarrier}
                onChange={(e) => setCustomCarrier(e.target.value)}
                placeholder="Enter carrier name"
              />
            </FormControl>
          )}

          <FormControl>
            <FormLabel>Tracking Number or FedEx URL</FormLabel>
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              onPaste={handlePaste}
              placeholder="Paste tracking number or URL"
            />
            {isTrackingValid && (
              <Text mt={2} fontSize="sm" color="gray.600">
                <strong>Tracking Number:</strong> {trackingNumber}
              </Text>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            ml={3}
            isLoading={submitting}
            onClick={handleSubmit}
            isDisabled={!isTrackingValid}
          >
            Update
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpdateShippingModal;
